// The referral programme's client-side decisions, with no Angular in them.
//
// This is the plain half of `ReferralService`, following the convention
// `deep-link-signin.ts` sets: the decisions and their tests live here, and the
// thin `.service.ts` sibling supplies the injected dependencies. Everything in
// this file is testable without a browser.
//
// See design-docs/referral-program.md.
import { ReferralClaimRefusal } from '@cf-shared/data-model/referral';

/**
 * Where the invite value waits while the friend creates an account.
 *
 * Signing up navigates and reloads the page, so the `ref` in the address bar is
 * gone by the time we can claim it. `sessionStorage` rather than `localStorage`
 * on purpose: it is scoped to the tab, so an invite one person opened cannot be
 * claimed by whoever signs in next in a different tab, and it clears itself when
 * the tab closes rather than lingering for months.
 *
 * **A correction to expect if you go looking at `coach-claim.service.ts` for the
 * precedent.** It solves the same problem but does NOT store its token this way —
 * the token stays in the address bar, and `COACH_CLAIM_PENDING_RELOAD_KEY` is a
 * flag saying a reload is pending, not the token itself. Read it for how the whole
 * arrive-reload-claim sequence hangs together; the storage part is written fresh
 * here.
 */
export const REFERRAL_PENDING_REF_KEY = 'cs_referral_pending_ref';

/**
 * How long a stored invite is still worth claiming, in hours.
 *
 * The value survives a reload, but it should not survive somebody leaving the tab
 * open overnight, coming back, and signing in to an unrelated account — that would
 * attach a referral neither person meant. A few hours comfortably covers following
 * a link and finishing a sign-up, including an email confirmation round trip.
 */
export const REFERRAL_PENDING_REF_MAX_AGE_HOURS = 6;

/** What is kept in session storage: the invite, and when it was put there. */
interface StoredPendingRef {
  ref: string;
  storedAt_utc: string;
}

/**
 * A minimal view of `sessionStorage`, so these decisions can be tested with a
 * plain object and so a browser that refuses storage cannot crash the page.
 */
export interface RefStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * What the pending-invite bridge could not do. The values are the condition names the service
 * reports under, so they stay stable.
 */
export enum PendingRefStorageProblem {
  /** The storage refused the write (Safari private mode does this). */
  NotStored = 'ReferralPendingRefNotStored',
  /** The storage refused the read. */
  Unreadable = 'ReferralPendingRefUnreadable',
  /** The stored value was not valid JSON, and was cleared. */
  DamagedCleared = 'ReferralPendingRefDamagedCleared',
}

/** Receives each storage problem, with the error behind it. */
export type PendingRefProblemReporter = (problem: PendingRefStorageProblem, err: unknown) => void;

/**
 * Remembers an invite across the sign-up reload.
 *
 * A storage failure does not throw: Safari in private mode throws on `setItem`,
 * and the consequence here is only that the friend's referral is not attached
 * automatically — they can still type the code on the hub. Failing the whole
 * invite page over it would be far worse. It goes to `onStorageProblem` instead,
 * so we can see how often invites are lost this way (#5191).
 */
export function rememberPendingRef(storage: RefStorage, ref: string, now_utc: string, onStorageProblem: PendingRefProblemReporter): void {
  try {
    const payload: StoredPendingRef = { ref, storedAt_utc: now_utc };
    storage.setItem(REFERRAL_PENDING_REF_KEY, JSON.stringify(payload));
  } catch (err) {
    onStorageProblem(PendingRefStorageProblem.NotStored, err);
  }
}

/**
 * Reads back an invite stored before sign-up, if one is still worth claiming.
 *
 * Returns undefined for every unusable state — nothing stored, unreadable text, a
 * missing timestamp, or one too old. An unreadable value is also REMOVED, so a
 * single corrupt entry cannot sit there being re-read on every page load.
 */
export function takePendingRef(storage: RefStorage, now_utc: string, onStorageProblem: PendingRefProblemReporter): string | undefined {
  let raw: string | null = null;
  try {
    raw = storage.getItem(REFERRAL_PENDING_REF_KEY);
  } catch (err) {
    onStorageProblem(PendingRefStorageProblem.Unreadable, err);
    return undefined;
  }
  if (!raw) return undefined;

  const clear = () => {
    try {
      storage.removeItem(REFERRAL_PENDING_REF_KEY);
    } catch {
      // not reported: the value is already being ignored, and one left behind is only cleared again next read
    }
  };

  let parsed: StoredPendingRef | undefined;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    clear();
    onStorageProblem(PendingRefStorageProblem.DamagedCleared, err);
    return undefined;
  }

  if (!parsed || typeof parsed.ref !== 'string' || !parsed.ref || typeof parsed.storedAt_utc !== 'string') {
    clear();
    return undefined;
  }

  const storedAt = Date.parse(parsed.storedAt_utc);
  const now = Date.parse(now_utc);
  if (Number.isNaN(storedAt) || Number.isNaN(now)) {
    clear();
    return undefined;
  }

  const ageHours = (now - storedAt) / (1000 * 60 * 60);
  if (ageHours > REFERRAL_PENDING_REF_MAX_AGE_HOURS) {
    clear();
    return undefined;
  }

  // taken, not merely read: claiming is attempted once, and a failure that leaves
  // it stored would retry on every page load for as long as the tab is open
  clear();
  return parsed.ref;
}

/**
 * Builds the message that goes into the share sheet.
 *
 * The link is deliberately NOT included in the text. Android's share plugin joins
 * the message and the link itself — `text + ' ' + url` — so a message ending in
 * the link would show it twice. `trySystemShare` carries the two separately and
 * its doc comment says so.
 */
export function buildShareMessage(messageWithoutLink: string): string {
  return messageWithoutLink.trim();
}

/**
 * Does this claim answer prove the caller now has a referral record (#4963)?
 *
 * `claimed` covers the two ordinary successes — the call wrote the record, or an
 * earlier one already had. The refusal below is the case that is easy to miss:
 * `AlreadyReferredByAnother` is refused PRECISELY BECAUSE the caller already has a
 * record, naming somebody else. Somebody who types a second, different code gets
 * `claimed: false` and is still a referred friend whose training their inviter
 * should see, so the marker must be armed for them too.
 *
 * Every other refusal means there is no record: an unknown invite, their own code,
 * a subscription of their own, an account too old, an inviter who is gone.
 */
export function provesCallerIsReferred(
  result: { claimed: boolean; refusal?: ReferralClaimRefusal } | null | undefined,
): boolean {
  if (!result) return false;
  if (result.claimed) return true;
  return result.refusal === ReferralClaimRefusal.AlreadyReferredByAnother;
}

/**
 * What the app knows when a workout has just been completed (#4963 phase 3).
 *
 * Gathered by the caller so the rule below can be driven by a test with no
 * Angular, no server and no settings service — the same shape
 * `shouldMarkFoodLoggedEver` in `telemetry-profile-props.ts` uses, and for the
 * same reason: every one of these refusals is silent, so each needs pinning.
 */
export interface TrainingReportFacts {
  /** Somebody invited this user and their claim succeeded. */
  wasReferred: boolean;
  /** The server has already confirmed their training flag. */
  alreadyReported: boolean;
  /**
   * A coach is managing somebody else's account in this session.
   *
   * The single most important refusal here. In that mode the settings in memory
   * belong to the CLIENT, but a server call still authenticates as the COACH — so
   * reporting would set the training flag on the coach's own referral record
   * because their client trained. Wrong person, wrong record, and nothing would
   * report it.
   */
  isManagingClient: boolean;
  /** Somebody is signed in. There is no caller for the server to identify otherwise. */
  hasSignedInUser: boolean;
  /**
   * The settings have finished loading.
   *
   * Before that every marker still reads as its default `false`, so asking would
   * answer "not referred" for somebody who is.
   */
  settingsLoaded: boolean;
}

/**
 * Whether to tell the server this user has started training.
 *
 * The gate exists because the app cannot see the referrals database: without it
 * every user would make this call after every workout for ever, and almost all of
 * them would be refused. `wasReferred` is what narrows it to people an invite
 * actually brought in.
 *
 * **It gates on RECORDED success, never on having tried.** Both earlier phases let
 * somebody claim an invite after they already have an account, by typing a code on
 * the hub or on the subscribe page — so a friend can create an account, train, and
 * only then type the code. A rule that disarmed itself on the first attempt would
 * make that person's one call the one that was correctly refused, and they would
 * sit on "Joined" for ever while they trained.
 */
export function shouldReportTrainingStarted(facts: TrainingReportFacts): boolean {
  if (!facts.settingsLoaded) return false;
  if (!facts.hasSignedInUser) return false;
  if (facts.isManagingClient) return false;
  if (!facts.wasReferred) return false;
  return !facts.alreadyReported;
}
